import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Fase 4 — Permissões de plataforma.
 *
 * Fonte única de verdade sobre "o usuário está presente em um estabelecimento
 * agora?". A localização do Zappy só pode ser coletada/persistida enquanto
 * existe um check-in ativo (não expirado e sem checkout), o que sustenta a
 * justificativa de uso de localização em primeiro plano nas lojas.
 */
export interface ActiveCheckInState {
  isCheckedIn: boolean;
  venueId: string | null;
  checkInId: string | null;
  expiresAt: string | null;
  declaredIntention: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const POLL_INTERVAL_MS = 60_000;

export const useActiveCheckInState = (userId: string | undefined): ActiveCheckInState => {
  const [state, setState] = useState<{
    venueId: string | null;
    checkInId: string | null;
    expiresAt: string | null;
    declaredIntention: string | null;
  }>({ venueId: null, checkInId: null, expiresAt: null, declaredIntention: null });
  const [loading, setLoading] = useState<boolean>(Boolean(userId));
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!userId) {
      setState({ venueId: null, checkInId: null, expiresAt: null, declaredIntention: null });
      setLoading(false);
      return;
    }
    try {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from("check_ins")
        .select("id, venue_id, expira_em, declared_intention")
        .eq("user_id", userId)
        .is("checked_out_at", null)
        .or(`expira_em.is.null,expira_em.gt.${nowIso}`)
        .order("checked_in_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!mountedRef.current) return;
      if (error || !data) {
        setState({ venueId: null, checkInId: null, expiresAt: null, declaredIntention: null });
      } else {
        setState({
          venueId: data.venue_id ?? null,
          checkInId: data.id ?? null,
          expiresAt: (data as { expira_em?: string | null }).expira_em ?? null,
          declaredIntention: data.declared_intention ?? null,
        });
      }
    } catch {
      if (mountedRef.current) {
        setState({ venueId: null, checkInId: null, expiresAt: null, declaredIntention: null });
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void refresh();
    if (!userId) return;
    const id = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh, userId]);

  // Reage imediatamente a check-in/checkout feitos nesta sessão.
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`active-checkin-${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "check_ins", filter: `user_id=eq.${userId}` },
        () => void refresh()
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, refresh]);

  useEffect(() => {
    const onUpdate = () => void refresh();
    window.addEventListener("checkin:updated", onUpdate);
    return () => window.removeEventListener("checkin:updated", onUpdate);
  }, [refresh]);

  return {
    isCheckedIn: Boolean(state.checkInId),
    venueId: state.venueId,
    checkInId: state.checkInId,
    expiresAt: state.expiresAt,
    declaredIntention: state.declaredIntention,
    loading,
    refresh,
  };
};

export default useActiveCheckInState;
